// src/features/test-runs/api/useTestRuns.ts
// Test Run hooks — CRUD + lifecycle actions
// All endpoints verified against openapi.json (testrun-service)
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type { TestRun, TestRunsResponse } from '@/types/testrun.types'

// Re-export so existing imports from this file still work
export type { TestRun, TestRunsResponse }

export interface TestRunFilters {
  testplan_id?: number
  build_id?: number
  status?: string
  started_by?: number
  limit?: number
  offset?: number
}

// ============================================================================
// Query Keys
// ============================================================================

export const testRunKeys = {
  all: ['testRuns'] as const,
  lists: () => [...testRunKeys.all, 'list'] as const,
  list: (filters?: TestRunFilters) => [...testRunKeys.lists(), filters] as const,
}

// ============================================================================
// List Test Runs
// GET /api/testruns
// ============================================================================

export const useTestRuns = (filters?: TestRunFilters) =>
  useQuery<TestRunsResponse>({
    queryKey: testRunKeys.list(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<TestRunsResponse>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.LIST,
        { params: filters }
      )
      return data
    },
  })

// ============================================================================
// Delete Test Run
// DELETE /api/testruns/{id} — 204
// ============================================================================

export const useDeleteTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API_CONFIG.ENDPOINTS.TEST_RUNS.DELETE(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.all })
    },
  })
}

// ============================================================================
// Start Test Run
// POST /api/testruns/{id}/start
// ============================================================================

export const useStartTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.post(API_CONFIG.ENDPOINTS.TEST_RUNS.START(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.all })
    },
  })
}

// ============================================================================
// Complete Test Run
// POST /api/testruns/{id}/complete
// ============================================================================

export const useCompleteTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.post(API_CONFIG.ENDPOINTS.TEST_RUNS.COMPLETE(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.all })
    },
  })
}

// ============================================================================
// Cancel Test Run
// POST /api/testruns/{id}/cancel
// ============================================================================

export const useCancelTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.post(API_CONFIG.ENDPOINTS.TEST_RUNS.CANCEL(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testRunKeys.all })
    },
  })
}
